import { useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTrans } from "../../../hooks/useTrans";
import { setPasswordPageSchema } from "./schema";
import {
    Button,
    Input,
    VStack,
    Box,
    Image,
    useToast,
    Center,
    Checkbox,
    useDisclosure,
    Text,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
} from "@chakra-ui/react";
import { User, UserApi } from "../../../apis";
import { AppFormControl, AppLoader } from "../../../components";
import { cPath } from "../../../config";
import logoSrc from "../../../assets/images/logo.svg";

export const SetPasswordPage = () => {
    // hooks
    const { t } = useTrans();
    const toast = useToast();
    const { code } = useParams();
    const navigate = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure();

    // state
    const [loading, setLoading] = useState(false);

    // form
    const {
        handleSubmit,
        register,
        formState: { errors, isSubmitting },
        reset,
    } = useForm<User>({
        resolver: yupResolver(setPasswordPageSchema(t)),
        mode: "onSubmit",
    });

    const onSubmitHandler = (formData: User) => {
        setLoading(true);
        formData.changePwdCode = code;
        UserApi.postUserSetPassword<User, User>(formData)
            .then(({ response, errorMessage }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    toast({
                        title: t("ppub.Security.SetPasswordPage:toast.success"),
                        status: "success",
                    });
                    reset();
                    navigate(cPath("PUBLIC.SECURITY.LOGIN_PAGE"));
                }
            })
            .finally(() => setLoading(false));
    };

    const renderTermsAndConditionModal = () => {
        return (
            <>
                <Modal
                    isOpen={isOpen}
                    onClose={onClose}
                    scrollBehavior={"inside"}
                    closeOnOverlayClick={false}
                    trapFocus={false}
                    isCentered
                >
                    <ModalOverlay />
                    <ModalContent
                        minW={{
                            base: "full",
                            sm: "80vw",
                            md: "70vw",
                            lg: "60vw",
                            xl: "50vw",
                        }}
                        maxH={"80vh"}
                    >
                        <ModalHeader>
                            {t(
                                "ppub.Security.SetPasswordPage:modal.termsAndCondition.title"
                            )}
                        </ModalHeader>
                        <ModalCloseButton />
                        <ModalBody
                            dangerouslySetInnerHTML={{
                                __html: t(
                                    "ppub.Security.SetPasswordPage:modal.termsAndCondition.content"
                                ),
                            }}
                        />
                        <ModalFooter>
                            <Button variant="outline" onClick={onClose}>
                                {t("cmn:button.close")}
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            </>
        );
    };

    if (loading) {
        return <AppLoader />;
    }

    return (
        <>
            <Box
                maxW={"28rem"}
                py={{ base: 8, sm: 8 }}
                mx={"auto"}
                mt={{ base: 0, sm: 8 }}
            >
                <Center>
                    <Image w={"60%"} src={logoSrc} alt="IMS" loading="lazy" />
                </Center>
            </Box>
            <Box
                maxW={"28rem"}
                py={{ base: 8, sm: 8 }}
                px={{ base: 4, sm: 10 }}
                borderRadius={"0.5rem"}
                boxShadow={"var(--chakra-colors-boxShadowPrimary)"}
                mx={"auto"}
                bg={"var(--chakra-colors-white)"}
            >
                <form onSubmit={handleSubmit(onSubmitHandler)}>
                    <VStack gap={5} alignItems={"stretch"}>
                        <AppFormControl
                            label={t("ent.User:plainPassword.label")}
                            isInvalid={!!errors?.plainPassword}
                            message={errors?.plainPassword?.message}
                        >
                            <Input
                                type="password"
                                placeholder={t(
                                    "ent.User:plainPassword.placeholder"
                                )}
                                {...register("plainPassword")}
                            />
                        </AppFormControl>
                        <AppFormControl
                            label={t("ent.User:plainPasswordConfirm.label")}
                            isInvalid={!!errors?.plainPasswordConfirm}
                            message={errors?.plainPasswordConfirm?.message}
                        >
                            <Input
                                type="password"
                                placeholder={t(
                                    "ent.User:plainPasswordConfirm.placeholder"
                                )}
                                {...register("plainPasswordConfirm")}
                            />
                        </AppFormControl>
                        <AppFormControl
                            isInvalid={!!errors?.isOptin}
                            message={errors?.isOptin?.message}
                        >
                            <Checkbox {...register("isOptin")}>
                                <>
                                    <Text textStyle={"regularSm"}>
                                        {t(
                                            "ppub.Security.SetPasswordPage:text.acceptTerms"
                                        )}
                                        <NavLink
                                            to={"#"}
                                            onClick={onOpen}
                                            style={{
                                                textDecoration: "underline",
                                            }}
                                        >
                                            <Text
                                                as={"span"}
                                                textStyle={"regularSm"}
                                            >
                                                {t(
                                                    "ppub.Security.SetPasswordPage:text.termsAndConditions"
                                                )}
                                            </Text>
                                        </NavLink>
                                    </Text>
                                </>
                            </Checkbox>
                        </AppFormControl>
                        <Button
                            isDisabled={isSubmitting || loading}
                            type="submit"
                        >
                            {t(
                                "ppub.Security.SetPasswordPage:button.setNewPassword"
                            )}
                        </Button>
                    </VStack>
                </form>
            </Box>
            {isOpen && renderTermsAndConditionModal()}
        </>
    );
};
